import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Save, CheckCircle2, AlertTriangle, Upload, Trash2, Shield, Smartphone, KeyRound, FileKey
} from "lucide-react";
import { API } from "@/lib/api";


function StatusBadge({ configured }) {
  return configured ? (
    <Badge className="bg-purple-100 text-success border-success/30 gap-1" variant="outline">
      <CheckCircle2 size={10} /> Configured
    </Badge>
  ) : (
    <Badge className="bg-purple-50 text-ink border-warning/40 gap-1" variant="outline">
      <AlertTriangle size={10} /> Not Configured
    </Badge>
  );
}

export default function SettingsPage() {
  const { authHeaders } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apple form
  const [appleForm, setAppleForm] = useState({ team_id: "", pass_type_id: "", organization_name: "", cert_password: "" });
  const [appleCert, setAppleCert] = useState(null);
  const [appleSaving, setAppleSaving] = useState(false);
  const appleCertRef = useRef(null);

  // Google form
  const [googleForm, setGoogleForm] = useState({ issuer_id: "" });
  const [googleSA, setGoogleSA] = useState(null);
  const [googleSaving, setGoogleSaving] = useState(false);
  const googleSARef = useRef(null);

  const [message, setMessage] = useState(null);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/wallet`, { headers: authHeaders() });
      setWallet(data);
      if (data.apple?.configured) {
        setAppleForm({
          team_id: "",
          pass_type_id: data.apple.pass_type_id || "",
          organization_name: data.apple.organization_name || "",
          cert_password: "",
        });
      }
      if (data.google?.configured) {
        setGoogleForm({ issuer_id: "" });
      }
    } catch {
      setWallet({ apple: { configured: false }, google: { configured: false } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const showMsg = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleAppleSave = async (e) => {
    e.preventDefault();
    if (!appleForm.team_id || !appleForm.pass_type_id || !appleForm.organization_name) {
      showMsg("Please fill all Apple Wallet fields", "error");
      return;
    }
    setAppleSaving(true);
    const fd = new FormData();
    fd.append("team_id", appleForm.team_id);
    fd.append("pass_type_id", appleForm.pass_type_id);
    fd.append("organization_name", appleForm.organization_name);
    fd.append("cert_password", appleForm.cert_password);
    if (appleCert) fd.append("certificate", appleCert);
    try {
      await axios.put(`${API}/settings/wallet/apple`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      showMsg("Apple Wallet credentials saved successfully");
      await fetchSettings();
      setAppleCert(null);
      if (appleCertRef.current) appleCertRef.current.value = "";
    } catch (err) {
      showMsg(err.response?.data?.detail || "Failed to save Apple credentials", "error");
    } finally {
      setAppleSaving(false);
    }
  };

  const handleGoogleSave = async (e) => {
    e.preventDefault();
    if (!googleForm.issuer_id) {
      showMsg("Please enter the Google Wallet Issuer ID", "error");
      return;
    }
    setGoogleSaving(true);
    const fd = new FormData();
    fd.append("issuer_id", googleForm.issuer_id);
    if (googleSA) fd.append("service_account", googleSA);
    try {
      await axios.put(`${API}/settings/wallet/google`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      showMsg("Google Wallet credentials saved successfully");
      await fetchSettings();
      setGoogleSA(null);
      if (googleSARef.current) googleSARef.current.value = "";
    } catch (err) {
      showMsg(err.response?.data?.detail || "Failed to save Google credentials", "error");
    } finally {
      setGoogleSaving(false);
    }
  };

  const handleClear = async (platform) => {
    if (!window.confirm(`Clear all ${platform === "apple" ? "Apple" : "Google"} Wallet credentials? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/settings/wallet/${platform}`, { headers: authHeaders() });
      showMsg(`${platform === "apple" ? "Apple" : "Google"} Wallet credentials cleared`);
      await fetchSettings();
    } catch (err) {
      showMsg(err.response?.data?.detail || "Failed to clear credentials", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="animate-spin text-ink/40" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-ink flex items-center justify-center">
          <Shield size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-ink">Platform Settings</h1>
          <p className="text-sm text-ink/50">Manage wallet integrations and platform credentials</p>
        </div>
      </div>

      {message && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${message.type === "error" ? "bg-danger/10 text-danger border border-danger/30" : "bg-purple-100 text-success border border-success/30"}`}
          data-testid="settings-message">
          {message.type === "error" ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
          {message.text}
        </div>
      )}

      {/* Apple Wallet */}
      <Card className="border-ink/10" data-testid="apple-wallet-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-ink flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              </div>
              <div>
                <CardTitle className="text-base">Apple Wallet</CardTitle>
                <p className="text-xs text-ink/50 mt-0.5">PassKit / .pkpass file generation</p>
              </div>
            </div>
            <StatusBadge configured={wallet?.apple?.configured} />
          </div>
        </CardHeader>
        <CardContent>
          {wallet?.apple?.configured && (
            <div className="mb-4 p-3 rounded-lg bg-surface border border-ink/5 text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-ink/50">Team ID</span>
                <span className="font-mono text-ink/80">{wallet.apple.team_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink/50">Pass Type</span>
                <span className="font-mono text-ink/80">{wallet.apple.pass_type_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink/50">Organization</span>
                <span className="text-ink/80">{wallet.apple.organization_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink/50">Certificate</span>
                <span className={wallet.apple.certificate_missing ? "text-amber-700 font-medium" : "text-ink/80"}>
                  {wallet.apple.certificate_missing
                    ? "Missing from storage — re-upload .p12"
                    : wallet.apple.has_certificate
                      ? "Uploaded"
                      : "Not uploaded"}
                </span>
              </div>
              {wallet.apple.certificate_missing && (
                <p className="text-amber-700/90 pt-1">
                  The signing certificate path is saved, but the file is gone (common after redeploy without persistent storage). Choose the .p12 below and Save again.
                </p>
              )}
              {wallet.apple.updated_at && (
                <div className="flex justify-between pt-1 border-t border-ink/5">
                  <span className="text-ink/40">Last updated</span>
                  <span className="text-ink/50">{new Date(wallet.apple.updated_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}
          <form onSubmit={handleAppleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Team ID</Label>
                <Input placeholder="e.g. A1B2C3D4E5" value={appleForm.team_id}
                  onChange={(e) => setAppleForm({ ...appleForm, team_id: e.target.value })}
                  data-testid="apple-team-id" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Pass Type Identifier</Label>
                <Input placeholder="e.g. pass.com.yourcompany.loyalty" value={appleForm.pass_type_id}
                  onChange={(e) => setAppleForm({ ...appleForm, pass_type_id: e.target.value })}
                  data-testid="apple-pass-type-id" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Organization Name</Label>
                <Input placeholder="e.g. Your Company Ltd" value={appleForm.organization_name}
                  onChange={(e) => setAppleForm({ ...appleForm, organization_name: e.target.value })}
                  data-testid="apple-org-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Certificate Password</Label>
                <Input type="password" placeholder="Certificate .p12 password" value={appleForm.cert_password}
                  onChange={(e) => setAppleForm({ ...appleForm, cert_password: e.target.value })}
                  data-testid="apple-cert-password" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest">
                <div className="flex items-center gap-1.5">
                  <FileKey size={12} />
                  Signing Certificate (.p12)
                </div>
              </Label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer" data-testid="apple-cert-upload">
                  <input ref={appleCertRef} type="file" accept=".p12,.pem,.cer" className="hidden"
                    onChange={(e) => setAppleCert(e.target.files?.[0] || null)} />
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-ink/10 bg-white hover:bg-surface transition-colors">
                    <Upload size={14} />
                    {appleCert ? appleCert.name : "Choose file"}
                  </span>
                </label>
                {appleCert && <span className="text-xs text-ink/50">{(appleCert.size / 1024).toFixed(1)} KB</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={appleSaving} className="bg-ink hover:bg-ink-soft text-white gap-1.5"
                data-testid="apple-save-button">
                {appleSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Apple Credentials
              </Button>
              {wallet?.apple?.configured && (
                <Button type="button" variant="outline" className="text-danger hover:text-danger hover:bg-danger/10 gap-1.5"
                  onClick={() => handleClear("apple")} data-testid="apple-clear-button">
                  <Trash2 size={14} /> Clear
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Google Wallet */}
      <Card className="border-ink/10" data-testid="google-wallet-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white border border-ink/10 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              </div>
              <div>
                <CardTitle className="text-base">Google Wallet</CardTitle>
                <p className="text-xs text-ink/50 mt-0.5">Google Pay API / Loyalty passes</p>
              </div>
            </div>
            <StatusBadge configured={wallet?.google?.configured} />
          </div>
        </CardHeader>
        <CardContent>
          {wallet?.google?.configured && (
            <div className="mb-4 p-3 rounded-lg bg-surface border border-ink/5 text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-ink/50">Issuer ID</span>
                <span className="font-mono text-ink/80">{wallet.google.issuer_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink/50">Service Account</span>
                <span className={wallet.google.service_account_missing ? "text-amber-700 font-medium" : "text-ink/80"}>
                  {wallet.google.service_account_missing
                    ? "Missing from storage — re-upload JSON"
                    : wallet.google.has_service_account
                      ? "Uploaded"
                      : "Not uploaded"}
                </span>
              </div>
              {wallet.google.service_account_missing && (
                <p className="text-amber-700/90 pt-1">
                  The service-account path is saved, but the file is gone. Upload the JSON key below and Save again.
                </p>
              )}
              {wallet.google.updated_at && (
                <div className="flex justify-between pt-1 border-t border-ink/5">
                  <span className="text-ink/40">Last updated</span>
                  <span className="text-ink/50">{new Date(wallet.google.updated_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}
          <form onSubmit={handleGoogleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest">Issuer ID</Label>
              <Input placeholder="e.g. 3388000000012345678" value={googleForm.issuer_id}
                onChange={(e) => setGoogleForm({ ...googleForm, issuer_id: e.target.value })}
                data-testid="google-issuer-id" />
              <p className="text-xs text-ink/40">Found in Google Pay & Wallet Console under Issuer settings</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest">
                <div className="flex items-center gap-1.5">
                  <KeyRound size={12} />
                  Service Account Key (JSON)
                </div>
              </Label>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer" data-testid="google-sa-upload">
                  <input ref={googleSARef} type="file" accept=".json" className="hidden"
                    onChange={(e) => setGoogleSA(e.target.files?.[0] || null)} />
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-ink/10 bg-white hover:bg-surface transition-colors">
                    <Upload size={14} />
                    {googleSA ? googleSA.name : "Choose file"}
                  </span>
                </label>
                {googleSA && <span className="text-xs text-ink/50">{(googleSA.size / 1024).toFixed(1)} KB</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={googleSaving} className="bg-ink hover:bg-ink-soft text-white gap-1.5"
                data-testid="google-save-button">
                {googleSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Google Credentials
              </Button>
              {wallet?.google?.configured && (
                <Button type="button" variant="outline" className="text-danger hover:text-danger hover:bg-danger/10 gap-1.5"
                  onClick={() => handleClear("google")} data-testid="google-clear-button">
                  <Trash2 size={14} /> Clear
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Info box */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3" data-testid="wallet-info-box">
        <Smartphone size={18} className="text-ink mt-0.5 shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-purple-900">How it works</p>
          <p className="text-xs text-purple-700 leading-relaxed">
            Once credentials are configured, customers will be able to add their loyalty card to their phone wallet directly from the customer portal.
            The pass will include their vendor branding (logo, colors), current points balance or stamp progress, and a scannable QR code for redemption.
          </p>
          <div className="flex gap-4 pt-1">
            <a href="https://developer.apple.com/wallet/" target="_blank" rel="noopener noreferrer"
              className="text-xs text-ink hover:text-purple-700 underline underline-offset-2">Apple Developer Docs</a>
            <a href="https://developers.google.com/wallet" target="_blank" rel="noopener noreferrer"
              className="text-xs text-ink hover:text-purple-700 underline underline-offset-2">Google Wallet API Docs</a>
          </div>
        </div>
      </div>
    </div>
  );
}
